import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { OWNERSHIP_DISCLAIMER } from "@/components/OwnershipDisclaimer";
import { toast } from "sonner";
import { Loader2, ShoppingBag, Euro, CheckCircle2, ExternalLink, Trash2, Ticket, Copy, Tag, Gift, Mail, ShieldAlert, Eye, Globe, Lock, Rocket } from "lucide-react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { CATEGORIES } from "@/lib/categories";
import PreviewPicker from "@/components/preview-picker/PreviewPicker";
import { PreviewField } from "@/components/preview-picker/PreviewField";
import { PublishDestination } from "@/components/groups/PublishDestination";
import BoostDialog from "@/components/BoostDialog";

const COMMISSION_RATE = 0.20;

export default function PublishDialog({
  open,
  onOpenChange,
  title,           // { title_id, name, item_count, published_product_id? }
  onPublished,     // fn(product) — parent refreshes titles
  onUnpublished,   // fn() — parent refreshes titles
}) {
  const [price, setPrice] = useState("2.99");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("other");
  const [isFree, setIsFree] = useState(false);
  const [freeEmailsText, setFreeEmailsText] = useState("");
  const [audience, setAudience] = useState("everyone");
  const [busy, setBusy] = useState(false);
  const [product, setProduct] = useState(null);
  const [aiError, setAiError] = useState("");
  const [boostOpen, setBoostOpen] = useState(false);
  const [noOwnership, setNoOwnership] = useState(false);
  const [preview, setPreview] = useState(null); // { blob, url, blur, id? }
  const [picking, setPicking] = useState(false);
  const [destGroup, setDestGroup] = useState(null);

  useEffect(() => {
    // Reset form fields ONLY when the dialog transitions to open. We
    // intentionally do NOT depend on `title` here — after publish, the
    // parent refetches titles which mutates the `title` prop; if that
    // triggered this effect, `product` (just set by submit()) would be
    // wiped and the success panel (with the share code) would never show.
    if (open) {
      setProduct(null);
      setPreview(null);
      setDestGroup(null);
      setPicking(false);
      setPrice("2.99");
      setDescription("");
      setCategory("other");
      setIsFree(false);
      setFreeEmailsText("");
      setAudience("everyone");
      setNoOwnership(false);
      setAiError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!title) return null;
  const alreadyPublished = !!title.published_product_id;

  const priceCents = () => {
    if (isFree) return 0;
    const p = parseFloat(price);
    if (isNaN(p)) return 0;
    return Math.round(p * 100);
  };
  const cents = priceCents();
  const commission = Math.round(cents * COMMISSION_RATE);
  const sellerCut = cents - commission;
  const parsedEmails = freeEmailsText
    .split(/[\s,;]+/)
    .map((e) => e.trim().toLowerCase())
    .filter((e) => e && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e));
  const MIN_DESC = 10;
  const descOk = description.trim().length >= MIN_DESC;
  const canSubmit =
    (isFree || (cents >= 99 && cents <= 99999)) &&
    descOk &&
    (audience === "everyone" || parsedEmails.length > 0) &&
    !busy;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setBusy(true);
    setAiError("");
    try {
      let previewId = preview?.id;
      if (preview && !previewId) {
        const fd = new FormData();
        fd.append("file", preview.blob, "preview.jpg");
        const up = await api.post(`/titles/${title.title_id}/preview-image`, fd);
        previewId = up.data.preview_id;
        setPreview((p) => (p ? { ...p, id: previewId } : p));
      }
      const r = await api.post(`/titles/${title.title_id}/publish`, {
        price_cents: cents,
        description: description.trim(),
        category,
        free_emails: parsedEmails,
        audience,
        no_ownership_claim: noOwnership,
        ...(previewId ? { preview_id: previewId, preview_blur: !!preview.blur } : {}),
        ...(destGroup ? { group_id: destGroup } : {}),
      });
      setProduct(r.data);
      toast.success(r.data.group_id ? `Published "${r.data.name}" to your group` : `Published "${r.data.name}" to the Marketplace`);
      onPublished?.(r.data);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      // 422 = the AI review decided the description doesn't match the content.
      // Surface it inline (not just a toast) so the seller knows what to fix.
      if (status === 422) {
        setAiError(detail || "Your description doesn't match the note's content. Please update it.");
      } else {
        toast.error(detail || "Publish failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const unpublish = async () => {
    if (!alreadyPublished || busy) return;
    if (!window.confirm(`Remove "${title.name}" from the Marketplace?`)) return;
    setBusy(true);
    try {
      await api.delete(`/titles/${title.title_id}/publish`);
      toast.success("Removed from Marketplace");
      onUnpublished?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unpublish failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
    {picking && (
      <PreviewPicker
        initialBlur={!!preview?.blur}
        onCancel={() => setPicking(false)}
        onDone={({ blob, blur }) => {
          setPreview((old) => { if (old?.url) URL.revokeObjectURL(old.url); return { blob, blur, url: URL.createObjectURL(blob) }; });
          setPicking(false);
        }}
      />
    )}
    {/* Hidden (not reset) while picking, so its scroll lock doesn't block the picker. */}
    <Dialog open={open && !picking} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="publish-dialog"
        className="bg-[#121212] border-[#27272A] text-[#F3F4F6] max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <ShoppingBag className="h-4 w-4 text-[#3B82F6]" />
            {(product?.group_id || (alreadyPublished && title?.published_group_id)) ? "Live in your group" : product || alreadyPublished ? "Live on Marketplace" : destGroup ? "Publish to group" : "Publish to Marketplace"}
          </DialogTitle>
        </DialogHeader>

        {product ? (
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-3 rounded-xl bg-[#1E1E1E] border border-[#27272A]">
              <CheckCircle2 className="h-5 w-5 text-[#3B82F6] mt-0.5 shrink-0" />
              <div className="text-sm text-[#D1D5DB]">
                <b className="text-[#F3F4F6]">{product.name}</b> {product.group_id ? "is now in your private group at" : "is now on the Marketplace at"}{" "}
                <b className="text-[#F3F4F6]">€{(product.price_cents / 100).toFixed(2)}</b>. {product.group_id ? "Only group members can find and preview your notes." : "Anyone can find and preview your notes."}
              </div>
            </div>
            {product.share_code && !product.group_id && (
              <div className="rounded-xl bg-[#1E1E1E] border border-[#27272A] p-3 flex items-center gap-3">
                <Ticket className="h-5 w-5 text-[#3B82F6] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-[#6B7280]">Invite code</div>
                  <div
                    data-testid="publish-share-code"
                    className="font-mono text-lg tracking-[0.25em] text-[#F3F4F6]"
                  >
                    {product.share_code}
                  </div>
                  <div className="text-[10px] text-[#9CA3AF] mt-0.5">
                    Share this with friends — they can paste it in Marketplace &rarr; Invite code to jump straight here.
                  </div>
                </div>
                <Button
                  type="button"
                  data-testid="publish-copy-code-btn"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(product.share_code);
                      toast.success("Code copied");
                    } catch { toast.error("Could not copy"); }
                  }}
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-full text-[#9CA3AF] hover:text-[#F3F4F6] hover:bg-[#27272A]"
                  aria-label="Copy code"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button
                data-testid="publish-boost-btn"
                type="button"
                onClick={() => setBoostOpen(true)}
                hidden={!!product.group_id}
                variant="ghost"
                className="rounded-full text-[#F5C518] hover:text-[#F5C518] hover:bg-[#1E1E1E] gap-1.5"
              >
                <Rocket className="h-3.5 w-3.5" />
                Promote
              </Button>
              <Link to={product?.group_id ? `/market?group=${product.group_id}` : "/market"} onClick={() => onOpenChange(false)}>
                <Button
                  data-testid="publish-view-market-btn"
                  className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white gap-1.5"
                >
                  View in Market <ExternalLink className="h-3.5 w-3.5" />
                </Button>
              </Link>
            </DialogFooter>
          </div>
        ) : alreadyPublished ? (
          <div className="space-y-4">
            <div className="text-sm text-[#9CA3AF]">
              This thread is live on the Marketplace. To change the price, unpublish first and re-publish at the new price.
            </div>
            {title.published_share_code && !title.published_group_id && (
              <div className="rounded-xl bg-[#1E1E1E] border border-[#27272A] p-3 flex items-center gap-3">
                <Ticket className="h-5 w-5 text-[#3B82F6] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-[#6B7280]">Invite code</div>
                  <div
                    data-testid="publish-share-code"
                    className="font-mono text-lg tracking-[0.25em] text-[#F3F4F6]"
                  >
                    {title.published_share_code}
                  </div>
                  <div className="text-[10px] text-[#9CA3AF] mt-0.5">
                    Share this with friends — they can paste it in Marketplace &rarr; Invite code to jump straight here.
                  </div>
                </div>
                <Button
                  type="button"
                  data-testid="publish-copy-code-btn"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(title.published_share_code);
                      toast.success("Code copied");
                    } catch { toast.error("Could not copy"); }
                  }}
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-full text-[#9CA3AF] hover:text-[#F3F4F6] hover:bg-[#27272A]"
                  aria-label="Copy code"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            )}
            <DialogFooter className="gap-2 flex-wrap">
              <Button
                data-testid="publish-boost-btn-existing"
                type="button"
                onClick={() => setBoostOpen(true)}
                hidden={!!title.published_group_id}
                variant="ghost"
                className="rounded-full text-[#F5C518] hover:text-[#F5C518] hover:bg-[#1E1E1E] gap-1.5"
              >
                <Rocket className="h-3.5 w-3.5" />
                Promote
              </Button>
              <Link to={title?.published_group_id ? `/market?group=${title.published_group_id}` : "/market"} onClick={() => onOpenChange(false)}>
                <Button
                  variant="ghost"
                  className="rounded-full text-[#9CA3AF] hover:text-[#F3F4F6] hover:bg-[#1E1E1E] gap-1.5"
                >
                  View in Market <ExternalLink className="h-3.5 w-3.5" />
                </Button>
              </Link>
              <Button
                data-testid="publish-unpublish-btn"
                onClick={unpublish}
                disabled={busy}
                variant="ghost"
                className="rounded-full text-[#FCA5A5] hover:text-[#FCA5A5] hover:bg-[#1E1E1E] gap-1.5"
              >
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                Unpublish
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="text-xs text-[#9CA3AF] leading-relaxed">
              Buyers get a full copy of <b className="text-[#F3F4F6]">{title.name}</b> ({title.item_count || 0} items) — all text, links, photos and files — delivered into their workspace.
            </div>

            <PreviewField preview={preview} onPick={() => setPicking(true)} onClear={() => setPreview(null)} />

            <div className="space-y-1.5">
              <Label htmlFor="price" className="text-xs text-[#9CA3AF]">Price (EUR)</Label>
              <div className="relative">
                <Euro className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
                <Input
                  id="price"
                  data-testid="publish-price-input"
                  type="number"
                  inputMode="decimal"
                  min="0.99"
                  max="999.99"
                  step="0.01"
                  required={!isFree}
                  disabled={isFree}
                  value={isFree ? "" : price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder={isFree ? "Free" : "2.99"}
                  className="bg-[#1E1E1E] border-[#27272A] h-11 rounded-xl pl-9 disabled:opacity-50"
                />
              </div>
              <p className="text-[11px] text-[#6B7280]">Minimum €0.99. Maximum €999.99.</p>
              <label className="flex items-center gap-2 text-xs text-[#D1D5DB] pt-1 select-none cursor-pointer">
                <Checkbox
                  data-testid="publish-free-checkbox"
                  checked={isFree}
                  onCheckedChange={(v) => setIsFree(v === true)}
                  className="border-[#3F3F46] data-[state=checked]:bg-[#10B981] data-[state=checked]:border-[#10B981]"
                />
                <Gift className="h-3.5 w-3.5 text-[#10B981]" />
                <span>{destGroup ? "Make this note free for group members" : "Make this note free for everyone"}</span>
              </label>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="description" className="text-xs text-[#9CA3AF]">
                Description <span className="text-[#F87171]">*</span>
              </Label>
              <textarea
                id="description"
                data-testid="publish-description-input"
                value={description}
                onChange={(e) => { setDescription(e.target.value); if (aiError) setAiError(""); }}
                rows={3}
                required
                placeholder="Describe exactly what buyers are getting — topics, how many items, what it helps with…"
                className="w-full resize-none bg-[#1E1E1E] border border-[#27272A] rounded-xl px-3 py-2 text-sm text-[#F3F4F6] placeholder:text-[#6B7280] focus:outline-none focus:border-[#3B82F6] focus:ring-1 focus:ring-[#3B82F6]"
              />
              <p className={`text-[11px] ${descOk ? "text-[#6B7280]" : "text-[#9CA3AF]"}`}>
                Required · at least {MIN_DESC} characters. Our AI checks it honestly matches your note before it goes live.
              </p>
              {aiError && (
                <div
                  data-testid="publish-ai-error"
                  className="rounded-xl bg-[#EF4444]/10 border border-[#EF4444]/40 px-3 py-2 text-xs text-[#FCA5A5] flex gap-2"
                >
                  <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" />
                  <div>
                    <b className="text-[#F87171]">Description doesn't match the note.</b>
                    <div className="mt-0.5 text-[#FCA5A5] whitespace-pre-wrap">{aiError}</div>
                  </div>
                </div>
              )}
              <label className="flex items-start gap-2 text-[11px] leading-snug text-[#9CA3AF] pt-1 select-none cursor-pointer">
                <Checkbox
                  data-testid="publish-no-ownership-checkbox"
                  checked={noOwnership}
                  onCheckedChange={(v) => setNoOwnership(v === true)}
                  className="mt-0.5 border-[#3F3F46] data-[state=checked]:bg-[#3B82F6] data-[state=checked]:border-[#3B82F6]"
                />
                <span>{OWNERSHIP_DISCLAIMER}</span>
              </label>
            </div>

            <PublishDestination value={destGroup} onChange={setDestGroup} />

            <div className="space-y-1.5">
              <Label htmlFor="category" className="text-xs text-[#9CA3AF] flex items-center gap-1">
                <Tag className="h-3 w-3" /> Category
              </Label>
              <div className="grid grid-cols-4 gap-1.5">
                {CATEGORIES.map(({ id, label, Icon }) => (
                  <button
                    key={id}
                    type="button"
                    data-testid={`publish-cat-${id}`}
                    onClick={() => setCategory(id)}
                    className={`flex flex-col items-center gap-1 rounded-lg border py-2 px-1 transition-all ${
                      category === id
                        ? "bg-[#2563EB] border-[#2563EB] text-white"
                        : "bg-[#1E1E1E] border-[#27272A] text-[#9CA3AF] hover:text-[#F3F4F6] hover:border-[#3F3F46]"
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    <span className="text-[10px]">{label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="free-emails" className="text-xs text-[#9CA3AF] flex items-center gap-1">
                <Mail className="h-3 w-3" />
                {audience === "invited" ? "People who can see this note" : destGroup ? "Free access for specific group members (optional)" : "Free access for specific friends (optional)"}
              </Label>
              <textarea
                id="free-emails"
                data-testid="publish-free-emails-input"
                value={freeEmailsText}
                onChange={(e) => setFreeEmailsText(e.target.value)}
                rows={2}
                placeholder={
                  audience === "invited"
                    ? "alice@example.com, bob@example.com"
                    : isFree ? (destGroup ? "Already free for group members" : "Already free for everyone") : "alice@example.com, bob@example.com"
                }
                disabled={isFree && audience === "everyone"}
                className="w-full resize-none bg-[#1E1E1E] border border-[#27272A] rounded-xl px-3 py-2 text-sm text-[#F3F4F6] placeholder:text-[#6B7280] focus:outline-none focus:border-[#3B82F6] focus:ring-1 focus:ring-[#3B82F6] disabled:opacity-50"
              />
              {audience === "invited" ? (
                <p className={`text-[11px] ${parsedEmails.length ? "text-[#10B981]" : "text-[#FCA5A5]"}`}>
                  {parsedEmails.length
                    ? `Only these ${parsedEmails.length} account${parsedEmails.length === 1 ? "" : "s"} will see this note${isFree ? "" : " — and they get it free"}.`
                    : "Add at least one email for a private note."}
                </p>
              ) : !isFree && parsedEmails.length > 0 ? (
                <p className="text-[11px] text-[#10B981]">
                  {parsedEmails.length} email{parsedEmails.length === 1 ? "" : "s"} will get this note for free.
                </p>
              ) : null}
            </div>

            {/* Who can find this listing — independent of the price */}
            <div className={`space-y-1.5 ${destGroup ? "hidden" : ""}`}>
              <Label className="text-xs text-[#9CA3AF] flex items-center gap-1">
                <Eye className="h-3 w-3" /> Who can see it
              </Label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: "everyone", icon: Globe, label: "Everyone", hint: "Listed in the Marketplace" },
                  { id: "invited", icon: Lock, label: "Only these people", hint: "Hidden from everyone else" },
                ].map(({ id, icon: Icon, label, hint }) => (
                  <button
                    key={id}
                    type="button"
                    data-testid={`publish-audience-${id}`}
                    onClick={() => setAudience(id)}
                    className={`text-left rounded-xl border px-3 py-2.5 transition-all ${
                      audience === id
                        ? "bg-[#2563EB]/15 border-[#3B82F6] text-[#F3F4F6]"
                        : "bg-[#1E1E1E] border-[#27272A] text-[#9CA3AF] hover:text-[#F3F4F6] hover:border-[#3F3F46]"
                    }`}
                  >
                    <span className="flex items-center gap-1.5 text-sm font-medium">
                      <Icon className="h-3.5 w-3.5" /> {label}
                    </span>
                    <span className="block text-[10px] text-[#6B7280] mt-0.5">{hint}</span>
                  </button>
                ))}
              </div>
            </div>

            {cents >= 99 && !isFree && (
              <div className="rounded-xl bg-[#1E1E1E] border border-[#27272A] p-3 text-xs text-[#D1D5DB] space-y-1">
                <div className="flex justify-between"><span className="text-[#9CA3AF]">List price</span><span>€{(cents / 100).toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-[#9CA3AF]">NoteLosh fee (20%)</span><span className="text-[#FCA5A5]">−€{(commission / 100).toFixed(2)}</span></div>
                <div className="flex justify-between font-medium border-t border-[#27272A] pt-1 mt-1">
                  <span className="text-[#9CA3AF]">You receive per sale</span>
                  <span className="text-[#3B82F6]">€{(sellerCut / 100).toFixed(2)}</span>
                </div>
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button
                type="button"
                onClick={() => onOpenChange(false)}
                variant="ghost"
                className="rounded-full text-[#9CA3AF] hover:text-[#F3F4F6] hover:bg-[#1E1E1E]"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="publish-submit-btn"
                disabled={!canSubmit}
                className={`rounded-full text-white ${
                  isFree ? "bg-[#10B981] hover:bg-[#059669]" : "bg-[#2563EB] hover:bg-[#1D4ED8]"
                }`}
              >
                {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Publishing…</> : (destGroup ? "Publish to Group" : isFree ? "Publish for free" : "Publish to Market")}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
      <BoostDialog
        open={boostOpen}
        onOpenChange={setBoostOpen}
        product={product || (alreadyPublished ? { product_id: title.published_product_id, name: title.name } : null)}
      />
    </Dialog>
    </>
  );
}
